/**
 * Watermark Store - manages watermark settings and applies watermarks to images.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const KEY = "khays_watermark_settings";

export interface WatermarkSettings {
  enabled: boolean;
  text: string;
  fontSize: number;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  color: string;
  opacity: number;
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";
}

function getDefaults(): WatermarkSettings {
  return {
    enabled: false,
    text: "Khay's Phones",
    fontSize: 24,
    fontFamily: "Arial",
    bold: true,
    italic: false,
    color: "#ffffff",
    opacity: 0.5,
    position: "bottom-right",
  };
}

function loadFromStorage(): WatermarkSettings {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) return { ...getDefaults(), ...JSON.parse(stored) };
  } catch {}
  return getDefaults();
}

interface WatermarkState extends WatermarkSettings {
  patch: (partial: Partial<WatermarkSettings>) => void;
  reload: () => void;
}

export const useWatermarkStore = create<WatermarkState>()(
  devtools(
    (set, get) => ({
      ...loadFromStorage(),
      patch: (partial) => {
        const { patch, reload, ...current } = get();
        const next = { ...current, ...partial };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(partial);
      },
      reload: () => set(loadFromStorage()),
    }),
    { name: "WatermarkSettings" }
  )
);

/** Apply watermark to an image file, returns a new Blob */
export async function applyWatermark(file: File): Promise<Blob> {
  const settings = useWatermarkStore.getState();
  if (!settings.enabled || !settings.text.trim()) return file;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }

      ctx.drawImage(img, 0, 0);

      // Setup text style
      const style = `${settings.italic ? "italic " : ""}${settings.bold ? "bold " : ""}${settings.fontSize}px ${settings.fontFamily}`;
      ctx.font = style;
      ctx.globalAlpha = settings.opacity;
      ctx.fillStyle = settings.color;

      const metrics = ctx.measureText(settings.text);
      const textW = metrics.width;
      const textH = settings.fontSize;
      const pad = 20;

      let x = pad, y = pad + textH;
      switch (settings.position) {
        case "top-right": x = canvas.width - textW - pad; y = pad + textH; break;
        case "bottom-left": x = pad; y = canvas.height - pad; break;
        case "bottom-right": x = canvas.width - textW - pad; y = canvas.height - pad; break;
        case "center": x = (canvas.width - textW) / 2; y = (canvas.height + textH) / 2; break;
      }

      // Draw shadow for readability
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.fillText(settings.text, x, y);

      canvas.toBlob(
        (blob) => blob ? resolve(blob) : resolve(file),
        file.type.includes("png") ? "image/png" : "image/jpeg",
        0.92
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}
